import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { CreditCard, Lock, Payment } from '@mui/icons-material';
import Cards from 'react-credit-cards-2';
import 'react-credit-cards-2/dist/es/styles-compiled.css';
import { PaymentFormData, Currency } from '../../types/PaymentLink';
import {
  validateCardNumber,
  validateExpiryDate,
  validateCVV,
  formatCardNumber,
  formatExpiryDate,
  detectCardType,
  formatCurrency,
  getCurrencySymbol,
} from '../../utils/validators';
import { initiate3DPayment } from '../../services/ziraatPayService';
import { toast } from 'react-toastify';

interface PaymentFormProps {
  amount: number;
  onSubmit: (paymentData: PaymentFormData) => Promise<void>;
  disabled?: boolean;
  allowPartialPayment?: boolean;
  depositAmount?: number;
  depositPaid?: boolean;
  remainingAmount?: number;
  currency?: Currency;
  useRealPayment?: boolean; // NEW - Enable real ZiraatPay payment gateway (default: false)
  paymentLinkId?: string; // Required for ZiraatPay integration
  linkId?: string; // The friendly link ID (e.g., ALX-2511211207-A7K9M) for callback URLs
}

interface FormErrors {
  cardNumber?: string;
  cardholderName?: string;
  expiryDate?: string;
  cvv?: string;
  amount?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  onSubmit,
  disabled = false,
  allowPartialPayment = false,
  depositAmount = 0,
  depositPaid = false,
  remainingAmount = 0,
  currency = 'EUR',
  useRealPayment = false, // TODO: Set to true when ZiraatPay is ready
  paymentLinkId,
  linkId,
}) => {
  // Debug logging
  console.log('PaymentForm - Props received:', {
    allowPartialPayment,
    depositAmount,
    depositPaid,
    remainingAmount,
    currency
  });

  // Initialize payment amount based on deposit status
  const getInitialAmount = () => {
    if (!allowPartialPayment) {
      // Partial payments not allowed
      if (!depositPaid) {
        // Deposit not paid yet - default to deposit amount (user can increase)
        return depositAmount;
      } else {
        // Deposit paid - must pay full remaining balance
        return remainingAmount;
      }
    } else {
      // Partial payments allowed - flexible amounts
      if (!depositPaid) {
        return depositAmount;
      }
      return remainingAmount;
    }
  };

  const [formData, setFormData] = useState<PaymentFormData>({
    cardNumber: '',
    cardholderName: '',
    expiryDate: '',
    cvv: '',
    amount: getInitialAmount(),
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [focused, setFocused] = useState<'name' | 'number' | 'expiry' | 'cvc' | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);

  // Calculate if amount should be readonly
  const amountReadonly = !allowPartialPayment && depositPaid;
  console.log('PaymentForm - Amount readonly?', amountReadonly, {
    allowPartialPayment,
    depositPaid
  });

  // Update amount when props change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      amount: getInitialAmount(),
    }));
  }, [depositPaid, remainingAmount, allowPartialPayment]);

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    if (value.length <= 16 && /^\d*$/.test(value)) {
      setFormData({
        ...formData,
        cardNumber: value,
      });
      if (errors.cardNumber) {
        setErrors({ ...errors, cardNumber: undefined });
      }
    }
  };

  const handleCardholderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setFormData({
      ...formData,
      cardholderName: value,
    });
    if (errors.cardholderName) {
      setErrors({ ...errors, cardholderName: undefined });
    }
  };

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 4) {
      setFormData({
        ...formData,
        expiryDate: formatExpiryDate(value),
      });
      if (errors.expiryDate) {
        setErrors({ ...errors, expiryDate: undefined });
      }
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cardType = detectCardType(formData.cardNumber);
    const maxLength = cardType === 'amex' ? 4 : 3;

    if (value.length <= maxLength && /^\d*$/.test(value)) {
      setFormData({
        ...formData,
        cvv: value,
      });
      if (errors.cvv) {
        setErrors({ ...errors, cvv: undefined });
      }
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Normalize decimal separator: replace comma with dot for consistency
    // This handles both European (1.234,56) and US (1,234.56) formats
    value = value.replace(/,/g, '.');
    
    // Parse the normalized value
    const numericValue = value === '' ? 0 : parseFloat(value);
    
    // Only update if it's a valid number (not NaN)
    if (!isNaN(numericValue)) {
      setFormData({
        ...formData,
        amount: numericValue,
      });
      if (errors.amount) {
        setErrors({ ...errors, amount: undefined });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate card number
    if (!formData.cardNumber) {
      newErrors.cardNumber = 'Card number is required';
    } else if (!validateCardNumber(formData.cardNumber)) {
      newErrors.cardNumber = 'Invalid card number';
    }

    // Validate cardholder name
    if (!formData.cardholderName.trim()) {
      newErrors.cardholderName = 'Cardholder name is required';
    } else if (formData.cardholderName.trim().length < 3) {
      newErrors.cardholderName = 'Name must be at least 3 characters';
    }

    // Validate expiry date
    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else if (!validateExpiryDate(formData.expiryDate)) {
      newErrors.expiryDate = 'Invalid or expired date';
    }

    // Validate CVV
    const cardType = detectCardType(formData.cardNumber);
    if (!formData.cvv) {
      newErrors.cvv = 'CVV is required';
    } else if (!validateCVV(formData.cvv, cardType)) {
      newErrors.cvv = `CVV must be ${cardType === 'amex' ? '4' : '3'} digits`;
    }

    // Validate amount
    if (!formData.amount || formData.amount <= 0 || isNaN(formData.amount)) {
      newErrors.amount = 'Please enter a valid payment amount';
    } else if (formData.amount > remainingAmount) {
      newErrors.amount = `Amount cannot exceed remaining balance (${formatCurrency(remainingAmount, currency)})`;
    } else if (!depositPaid && formData.amount < depositAmount) {
      // If deposit not paid, minimum is deposit amount (regardless of allowPartialPayment)
      newErrors.amount = `Minimum payment required: ${formatCurrency(depositAmount, currency)} (deposit)`;
    } else if (!allowPartialPayment && depositPaid && formData.amount !== remainingAmount) {
      // If partial payments not allowed AND deposit is paid, must pay full remaining balance
      newErrors.amount = `You must pay the full remaining balance: ${formatCurrency(remainingAmount, currency)}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (useRealPayment) {
        // Use 3D Secure payment flow
        await handle3DPayment(formData);
      } else {
        // Use mock payment flow
        await onSubmit(formData);
      }
    } catch (error) {
      console.error('Payment submission error:', error);
    } finally {
      if (!useRealPayment) {
        // Only reset loading for mock - 3D will redirect
        setLoading(false);
      }
    }
  };

  // Handle 3D Secure payment flow
  const handle3DPayment = async (formData: PaymentFormData) => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    // Validate required fields
    if (!paymentLinkId) {
      toast.error('Payment link ID is missing');
      return;
    }

    setLoading(true);

    try {
      // Format card expiry to MMYY
      const [month, year] = formData.expiryDate.split('/');
      const cardExpiry = `${month}${year}`;

      // Call backend to initiate 3D Secure
      const response = await initiate3DPayment({
        paymentLinkId,
        linkId: linkId || paymentLinkId, // Use linkId for callback URLs, fallback to paymentLinkId
        amount: formData.amount,
        currency,
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
        cardExpiry,
        cardCVV: formData.cvv,
        cardHolder: formData.cardholderName,
      });

      // Open 3D form in current window (will redirect to bank)
      const newWindow = window.open('', '_self');
      if (newWindow) {
        newWindow.document.write(response.htmlContent);
        newWindow.document.close();
      } else {
        // Fallback if popup blocked
        const blob = new Blob([response.htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.location.href = url;
      }
    } catch (error: any) {
      console.error('3D payment initiation failed:', error);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const currencySymbol = getCurrencySymbol(currency);

  return (
    <Card
      sx={{
        height: '100%',
        boxShadow: 3,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700, mb: 3 }}>
          {useRealPayment ? (
            <>
              <Payment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Secure Payment Gateway
            </>
          ) : (
            <>
              <CreditCard sx={{ mr: 1, verticalAlign: 'middle' }} />
              Payment Details
            </>
          )}
        </Typography>

        {/* Credit Card Form - Shown for both Real and Mock Payment */}
        {useRealPayment && (
          /* 3D Secure Mode - Show security info */
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: '#e3f2fd',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
              <Lock sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Ziraat Bank 3D Secure Payment
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              You will be redirected to the bank's 3D Secure page after entering your card details
            </Typography>
          </Box>
        )}

        {/* Common Credit Card Form */}
        {(useRealPayment || !useRealPayment) && (
          <>
            {/* Animated Credit Card */}
            <Box
              display="flex"
              justifyContent="center"
              sx={{
                mb: 4,
                '& .rccs': {
                  margin: '0 auto',
                },
                '& .rccs__card': {
                  maxWidth: { xs: '280px', sm: '350px' },
                },
              }}
            >
              <Cards
                number={formData.cardNumber}
                name={formData.cardholderName}
                expiry={formData.expiryDate}
                cvc={formData.cvv}
                focused={focused}
              />
            </Box>

            {/* Payment Form */}
            <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            {/* Amount Field - Editable or Read-only based on allowPartialPayment and deposit status */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount"
                type="text"
                value={formData.amount === 0 ? '' : formData.amount}
                onChange={handleAmountChange}
                placeholder="0.00"
                onFocus={(e) => {
                  // Select all on focus for easy editing (only if not empty)
                  if (e.target.value && e.target.value !== '0') {
                    e.target.select();
                  }
                }}
                onWheel={(e) => {
                  // Prevent scroll from changing the number - completely prevent default behavior
                  e.preventDefault();
                  (e.target as HTMLElement).blur();
                }}
                error={!!errors.amount}
                helperText={
                  errors.amount || (
                    !allowPartialPayment && !depositPaid && depositAmount > 0
                      ? `Pay deposit (minimum ${formatCurrency(depositAmount, currency)}) or pay more`
                      : !allowPartialPayment && depositPaid
                      ? 'Final payment - full remaining balance required'
                      : allowPartialPayment && !depositPaid && depositAmount > 0
                      ? `You can pay between ${formatCurrency(depositAmount, currency)} and ${formatCurrency(remainingAmount, currency)}`
                      : `You can pay any amount up to ${formatCurrency(remainingAmount, currency)}`
                  )
                }
                disabled={
                  (!allowPartialPayment && depositPaid) || // Readonly if partial not allowed and deposit paid
                  disabled ||
                  loading
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                  readOnly: !allowPartialPayment && depositPaid, // Readonly if must pay full balance
                }}
                inputProps={{
                  min: (!depositPaid && depositAmount > 0) 
                    ? depositAmount 
                    : (allowPartialPayment ? 1 : remainingAmount),
                  max: remainingAmount,
                  step: 0.01,
                }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Card Number"
                value={formatCardNumber(formData.cardNumber)}
                onChange={handleCardNumberChange}
                onFocus={() => setFocused('number')}
                error={!!errors.cardNumber}
                helperText={errors.cardNumber}
                placeholder="1234 5678 9012 3456"
                disabled={disabled || loading}
                required
                InputProps={{
                  startAdornment: <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Cardholder Name"
                value={formData.cardholderName}
                onChange={handleCardholderNameChange}
                onFocus={() => setFocused('name')}
                error={!!errors.cardholderName}
                helperText={errors.cardholderName}
                placeholder="Name Surname"
                disabled={disabled || loading}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                value={formData.expiryDate}
                onChange={handleExpiryDateChange}
                onFocus={() => setFocused('expiry')}
                error={!!errors.expiryDate}
                helperText={errors.expiryDate}
                placeholder="MM/YY"
                disabled={disabled || loading}
                required
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="CVV"
                value={formData.cvv}
                onChange={handleCvvChange}
                onFocus={() => setFocused('cvc')}
                error={!!errors.cvv}
                helperText={errors.cvv}
                placeholder="123"
                disabled={disabled || loading}
                required
                type="password"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={disabled || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <Lock />
                }
                sx={{
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1b5e20 0%, #388e3c 100%)',
                  },
                  minHeight: 56,
                }}
              >
                {loading
                  ? useRealPayment
                    ? 'Redirecting to 3D Secure...'
                    : 'Processing...'
                  : useRealPayment
                  ? `Proceed to 3D Secure - ${formatCurrency(formData.amount, currency)}`
                  : `Pay ${formatCurrency(formData.amount, currency)}`}
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Security Note */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            <Lock sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            {useRealPayment ? 'Ziraat Bank 3D Secure - 256-bit SSL Encrypted' : '256-bit SSL Encrypted Payment'}
          </Typography>
        </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentForm;
